
import { of as observableOf,  Observable ,  Subject } from 'rxjs'

import { filter, switchMap,  catchError ,  tap } from 'rxjs/operators'
import { useEffectUpdateOnly } from 'hooks/useEffectUpdateOnly'
import { PlannedPost } from 'interfaces'
import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { fetchPlannedPosts, PlannedPostFetchResponseData } from 'services/post/actions'
import {
  bucketsMapSelector,
  emptySlotsVisibleSelector,
  plannedPostsLayoutSelector,
  selectedProfilesFetchIdsSelector,
  virtualPostsVisibleSelector
} from 'services/post/selectors'
import { message } from 'services/snackbar'
import { StoreThunkDispatch } from 'store/state'
import { addPosts, setPosts, updatePostBuckets } from '../actions'
import { PostsAction, postsReducer } from '../reducer'
import { ContentType } from 'shared'

export function usePlannedPosts(
  search: { query: string, types: ContentType[], searchInSummary: boolean },
  viewFilter: string | undefined,
  onBeforeFetch?: () => void,
  onFetched?: (response: any) => void
) {
  const dispatch = useDispatch<StoreThunkDispatch>()
  const selectedProfilesFetchIds = useSelector(selectedProfilesFetchIdsSelector)
  const activeView = useSelector(plannedPostsLayoutSelector)
  const emptySlotsVisible = useSelector(emptySlotsVisibleSelector)
  const virtualPostsVisible = useSelector(virtualPostsVisibleSelector)
  const buckets = useSelector(bucketsMapSelector)

  const [posts, postsDispatch] = useReducer<PlannedPost[], [PostsAction]>(postsReducer, [])
  const [loading, setLoading] = useState(false)
  const [page, setPage] = useState(0)
  const [hasNextPage, setHasNextPage] = useState(true)
  const [hasNonEmptyPosts, setHasNonEmptyPosts] = useState(false)
  const [totalPostsCount, setTotalPostsCount] = useState(0)
  const [hasPendingPosts, setHasPendingPosts] = useState(false)
  const [refreshKey, setRefreshKey] = useState(0)

  // Use refs for callbacks to avoid dependency issues
  const onBeforeFetchRef = useRef(onBeforeFetch)
  const onFetchedRef = useRef(onFetched)

  useEffect(() => {
    onBeforeFetchRef.current = onBeforeFetch
    onFetchedRef.current = onFetched
  })

  // Serialize types array to avoid reference comparison issues
  const searchTypesKey = search.types.join(',')

  const fetch$ = useRef<Subject<{
    ids: string[],
    page: number,
    query?: string,
    types?: ContentType[],
    withEmptySlots: boolean,
    filter?: 'scheduled' | 'queued',
    searchInSummary?: boolean,
    withVirtualPosts?: boolean
  }>>(undefined)
  const subscription = useRef<any>(null)

  useEffectUpdateOnly(() => {
    postsDispatch(updatePostBuckets(buckets))
  }, [buckets])

  useEffect(() => {
    fetch$.current = new Subject()
    subscription.current = fetch$.current.pipe(
      filter(args => args.ids.length > 0))
      .pipe(
        tap(({ page }) => {
          setLoading(true)
          setPage(page)
        })
      ).pipe(
        switchMap(({ ids, page, query, types, withEmptySlots, filter, searchInSummary, withVirtualPosts }) => {
          const f = filter as any === 'planned' ? undefined : filter
          return dispatch(fetchPlannedPosts(page, ids, query, types, withEmptySlots, f, searchInSummary, withVirtualPosts))
            .pipe(
              catchError(() => {
                dispatch(message('Error fetching posts! Please refresh.', 'error'))
                return observableOf({ error: true })
              })
            )
        }))
      .subscribe((response: PlannedPostFetchResponseData) => {
        setLoading(false)
        if (onFetchedRef.current) {
          onFetchedRef.current(response)
        }

        if ((response as any).error) {
          return
        }

        setHasNextPage(response.posts.length > 0)
        setHasNonEmptyPosts(current => current || response.posts.some(p => !p.isEmpty))
        setTotalPostsCount(response.totalPostsCount)

        if (response.page === 0) {
          setHasPendingPosts(response.hasPendingPosts)
          postsDispatch(setPosts(response.posts))
        } else {
          postsDispatch(addPosts(response.posts))
        }
      })

    return () => {
      subscription.current?.unsubscribe()
      fetch$.current?.unsubscribe()
    }
  }, [dispatch])

  // Fetch first page of posts when profile selection changes or posts refresh is requested
  useEffect(() => {
    if (selectedProfilesFetchIds.length > 0) {
      const ids = selectedProfilesFetchIds.split(',')
      if (onBeforeFetchRef.current) {
        onBeforeFetchRef.current()
      }
      fetch$.current?.next({
        page: 0,
        ids,
        query: search.query,
        types: searchTypesKey.split(',').filter(Boolean) as ContentType[],
        withEmptySlots: emptySlotsVisible,
        filter: viewFilter as any,
        searchInSummary: search.searchInSummary,
        withVirtualPosts: virtualPostsVisible
      })
    }
  }, [
    selectedProfilesFetchIds,
    refreshKey,
    search.query,
    searchTypesKey,
    activeView,
    emptySlotsVisible,
    viewFilter,
    search.searchInSummary,
    virtualPostsVisible
  ])

  const loadNextPage = useCallback(() => {
    if (hasNextPage && !loading) {
      fetch$.current?.next({
        page: page + 1,
        ids: selectedProfilesFetchIds.split(','),
        query: search.query,
        types: search.types,
        filter: viewFilter as any,
        withEmptySlots: emptySlotsVisible,
        searchInSummary: search.searchInSummary,
        withVirtualPosts: virtualPostsVisible
      })
    }
  }, [page, selectedProfilesFetchIds, hasNextPage, loading, search, emptySlotsVisible, viewFilter, virtualPostsVisible])

  const refresh = useCallback(() => {
    setRefreshKey(key => key + 1)
  }, [])

  const postsMap = useMemo(() => new Map(posts.map(p => [p.id, p])), [posts])

  return {
    posts,
    postsMap,
    loading,
    hasNonEmptyPosts,
    totalPostsCount,
    hasPendingPosts,
    postsDispatch,
    loadNextPage,
    refresh
  }
}
